import React from "react";
import { useNavigate } from "react-router-dom";
import { FaShieldAlt, FaArrowLeft } from "react-icons/fa";
import "../../../assets/css/customer/privacyPolicy/PrivacyPolicy.css";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: (
      <>
        <p>To provide our services efficiently, we may collect the following information:</p>
        <ul className="pp-list">
          <li>Full Name</li>
          <li>Email Address</li>
          <li>Phone Number</li>
          <li>Date of Birth</li>
          <li>Gender</li>
          <li>Delivery Address / Location</li>
          <li>Login Credentials</li>
          <li>Order History</li>
          <li>Wishlist Items</li>
          <li>Product Reviews and Ratings</li>
          <li>Custom Furniture Request Details</li>
          <li>AI Room Design Images uploaded by customers</li>
          <li>Payment information required for secure online payments through Stripe</li>
          <li>Notification preferences (Email Notifications and App Notifications)</li>
        </ul>
        <p>This information is collected only to provide our services and improve the customer experience.</p>
      </>
    ),
  },
  {
    title: "2. How We Use Your Information",
    body: (
      <>
        <p>The information we collect is used for the following purposes:</p>
        <ul className="pp-list">
          <li>To create and manage customer accounts.</li>
          <li>To process and confirm customer orders.</li>
          <li>To securely process online payments through Stripe.</li>
          <li>To deliver furniture to the correct address.</li>
          <li>To provide AI-powered room design suggestions.</li>
          <li>To manage custom furniture requests and bidding activities.</li>
          <li>To respond to customer inquiries and support requests.</li>
          <li>To improve our website functionality and user experience.</li>
          <li>
            To send order confirmations, notifications, promotional offers, and
            important service updates based on the customer's notification
            preferences.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "3. Payment Security",
    body: (
      <>
        <p>
          AD Furniture House uses <strong>Stripe</strong> to process online
          payments securely.
        </p>
        <p>
          We do not store customers' complete debit or credit card information
          on our servers. All payment transactions are processed through
          Stripe using secure encryption and industry-standard security
          measures to protect customer payment information.
        </p>
      </>
    ),
  },
  {
    title: "4. AI Room Design Images",
    body: (
      <>
        <p>
          Customers can upload room images to receive AI-powered furniture
          placement and interior design suggestions.
        </p>
        <p>
          The uploaded images are stored securely in our database and are used
          only for generating personalized AI recommendations. These images
          are not shared, sold, or used for any purpose other than providing
          the requested AI design service.
        </p>
      </>
    ),
  },
  {
    title: "5. Data Protection",
    body: (
      <>
        <p>
          We take appropriate technical and security measures to protect
          customer information from unauthorized access, misuse,
          modification, or disclosure.
        </p>
        <p>
          Only authorized administrators are allowed to access customer
          information when required for order management, customer support,
          or website administration.
        </p>
      </>
    ),
  },
  {
    title: "6. Sharing of Information",
    body: (
      <>
        <p>AD Furniture House respects the privacy of its customers.</p>
        <p>
          We do not sell, rent, or share customers' personal information with
          third parties for advertising or marketing purposes.
        </p>
        <p>Customer information may only be shared when necessary to:</p>
        <ul className="pp-list">
          <li>Process secure online payments through Stripe.</li>
          <li>Complete product delivery.</li>
          <li>Comply with legal requirements.</li>
          <li>Protect the security and integrity of our website and services.</li>
        </ul>
      </>
    ),
  },
  {
    title: "7. Customer Rights",
    body: (
      <>
        <p>Customers have the following rights regarding their personal information:</p>
        <ul className="pp-list">
          <li>View their personal profile information.</li>
          <li>Update or edit their account details.</li>
          <li>Delete their account from the website.</li>
          <li>Manage their notification preferences.</li>
          <li>Contact our support team regarding any privacy-related concerns.</li>
        </ul>
        <p>
          We are committed to handling customer information responsibly and
          respecting user privacy.
        </p>
      </>
    ),
  },
  {
    title: "8. Data Retention",
    body: (
      <p>
        Customer information is stored only for as long as it is required to
        provide our services, maintain order records, manage transactions, and
        support website functionality. When customer information is no longer
        required, it will be managed according to our internal data
        management practices.
      </p>
    ),
  },
  {
    title: "9. Changes to This Privacy Policy",
    body: (
      <p>
        AD Furniture House may update this Privacy Policy from time to time
        to reflect changes in our website, services, or legal requirements.
        Any updates will become effective immediately after they are
        published on this website. Customers are encouraged to review this
        page periodically to stay informed about how their information is
        protected.
      </p>
    ),
  },
  {
    title: "10. Contact Us",
    body: (
      <p>
        If you have any questions, suggestions, or concerns regarding this
        Privacy Policy or the way your personal information is handled,
        please contact us through the{" "}
        <a href="/contact-us" className="pp-inline-link">
          Contact Us
        </a>{" "}
        page available on our website. Our support team will make every
        effort to respond to your inquiry as quickly as possible.
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="pp-page">
      {/* ── Back Button ── */}
      <button className="pp-back-btn" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back
      </button>

      {/* ── Header ── */}
      <div className="pp-header">
        <FaShieldAlt className="pp-header-icon" />
        <h1 className="pp-header-title">Privacy Policy</h1>
        <p className="pp-header-subtitle">
          Your trust matters to us. Here's how AD Furniture House collects,
          uses, and protects your information.
        </p>
      </div>

      {/* ── Content ── */}
      <div className="pp-content">
        <p className="pp-intro">
          Welcome to <strong>AD Furniture House</strong>. We value your
          privacy and are committed to protecting your personal information.
          This Privacy Policy explains how we collect, use, store, and
          protect the information you provide while using our website. By
          accessing or using our website, you agree to the terms outlined in
          this Privacy Policy.
        </p>

        {SECTIONS.map(({ title, body }, index) => (
          <section className="pp-section" key={index}>
            <h2 className="pp-section-title">{title}</h2>
            <div className="pp-section-body">{body}</div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default PrivacyPolicy;